/**
 * CampaignAttributionPage — Campaign source breakdown and performance metrics.
 * Shows source pie (rendered as donut), performance table, and funnel visualization.
 */

import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { RefreshCw, TrendingUp } from 'lucide-react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import api from '../services/api';

interface CampaignSource {
  campaign_source: string;
  campaign_name: string;
  contact_count: number;
}

interface CampaignPerformance {
  campaign_source: string;
  campaign_name: string;
  contacts_acquired: number;
  contacts_engaged: number;
  contacts_converted: number;
  revenue_generated: number;
  total_pipeline_value: number;
  engagement_rate: number;
  conversion_rate: number;
}

const PALETTE = [
  '#90b9ab', '#3b82f6', '#f59e0b', '#a855f7',
  '#22c55e', '#ef4444', '#06b6d4', '#f97316',
  '#8b5cf6', '#ec4899',
];

function fmtMoney(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

export default function CampaignAttributionPage() {
  const [sources, setSources] = useState<CampaignSource[]>([]);
  const [performance, setPerformance] = useState<CampaignPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [srcRes, perfRes] = await Promise.allSettled([
        api.get('/attribution/sources'),
        api.get('/attribution/performance'),
      ]);
      if (srcRes.status === 'fulfilled') setSources(srcRes.value.data || []);
      if (perfRes.status === 'fulfilled') setPerformance(perfRes.value.data || []);
    } catch {
      // degrade silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSeed = async () => {
    setSeeding(true);
    setSeedMsg('');
    try {
      const res = await api.post('/attribution/seed');
      setSeedMsg(`Seeded ${res.data.rows_inserted ?? 0} attribution records`);
      await loadData();
    } catch {
      setSeedMsg('Seed failed — check API logs');
    } finally {
      setSeeding(false);
    }
  };

  const totalContacts = sources.reduce((s, r) => s + r.contact_count, 0);

  // Funnel totals across all campaigns
  const funnelData = performance.reduce(
    (acc, p) => ({
      acquired: acc.acquired + p.contacts_acquired,
      engaged: acc.engaged + p.contacts_engaged,
      converted: acc.converted + p.contacts_converted,
    }),
    { acquired: 0, engaged: 0, converted: 0 }
  );

  const funnelBars = [
    { name: 'Acquired', value: funnelData.acquired, color: '#3b82f6' },
    { name: 'Engaged',  value: funnelData.engaged,  color: '#f59e0b' },
    { name: 'Converted', value: funnelData.converted, color: '#22c55e' },
  ];

  return (
    <div className="page-enter">
      <Header
        title="Campaign Attribution"
        subtitle="How your contacts were acquired and converted"
      />

      <div className="p-4 md:p-8 space-y-6">

        {/* Hero row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Attributed', value: totalContacts.toLocaleString(), color: '#90b9ab' },
            { label: 'Engaged',          value: funnelData.engaged.toLocaleString(), color: '#f59e0b' },
            { label: 'Converted',        value: funnelData.converted.toLocaleString(), color: '#22c55e' },
            {
              label: 'Total Revenue',
              value: fmtMoney(performance.reduce((s, p) => s + p.revenue_generated, 0)),
              color: '#a855f7',
            },
          ].map(card => (
            <div
              key={card.label}
              className="rounded-xl p-4"
              style={{ background: `${card.color}10`, border: `1px solid ${card.color}25` }}
            >
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                {card.label}
              </p>
              <span className="font-display text-2xl" style={{ color: card.color }}>
                {card.value}
              </span>
            </div>
          ))}
        </div>

        {/* Two-column: Pie + Funnel */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Source breakdown donut */}
          <Panel title="Source Breakdown">
            {loading ? (
              <div className="h-48 flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>
                Loading...
              </div>
            ) : sources.length === 0 ? (
              <div className="h-48 flex flex-col items-center justify-center gap-3">
                <TrendingUp size={28} style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No attribution data yet.</p>
                <button
                  onClick={handleSeed}
                  disabled={seeding}
                  className="text-xs px-3 py-1.5 rounded-lg"
                  style={{
                    background: 'rgba(144,185,171,0.1)',
                    border: '1px solid rgba(144,185,171,0.2)',
                    color: 'var(--netrun-green)',
                    cursor: 'pointer',
                  }}
                >
                  {seeding ? 'Seeding...' : 'Seed from existing contacts'}
                </button>
                {seedMsg && (
                  <p className="text-xs" style={{ color: 'var(--netrun-green)' }}>{seedMsg}</p>
                )}
              </div>
            ) : (
              <div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={sources}
                      dataKey="contact_count"
                      nameKey="campaign_name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={2}
                    >
                      {sources.map((_, i) => (
                        <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: 'var(--bg-input)',
                        border: '1px solid var(--border-primary)',
                        borderRadius: 8,
                        color: 'var(--text-primary)',
                        fontSize: 12,
                      }}
                      formatter={(val: any, name: any) => [
                        `${Number(val).toLocaleString()} contacts (${((Number(val) / totalContacts) * 100).toFixed(1)}%)`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div className="mt-2 space-y-1.5">
                  {sources.slice(0, 8).map((s, i) => (
                    <div key={s.campaign_source} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} />
                        <span style={{ color: 'var(--text-secondary)' }}>{s.campaign_name}</span>
                      </div>
                      <span style={{ color: 'var(--text-muted)' }}>
                        {s.contact_count.toLocaleString()}
                        {' '}
                        <span style={{ opacity: 0.6 }}>
                          ({((s.contact_count / Math.max(totalContacts, 1)) * 100).toFixed(1)}%)
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          {/* Funnel */}
          <Panel title="Conversion Funnel">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={funnelBars} barSize={48}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    borderRadius: 8,
                    color: 'var(--text-primary)',
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {funnelBars.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {[
                {
                  label: 'Engagement Rate',
                  value: fmtPct(funnelData.acquired > 0
                    ? (funnelData.engaged / funnelData.acquired) * 100
                    : 0),
                  color: '#f59e0b',
                },
                {
                  label: 'Conversion Rate',
                  value: fmtPct(funnelData.acquired > 0
                    ? (funnelData.converted / funnelData.acquired) * 100
                    : 0),
                  color: '#22c55e',
                },
                {
                  label: 'Sources',
                  value: String(sources.length),
                  color: '#90b9ab',
                },
              ].map(m => (
                <div
                  key={m.label}
                  className="rounded-lg p-2.5 text-center"
                  style={{ background: `${m.color}10`, border: `1px solid ${m.color}20` }}
                >
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                    {m.label}
                  </p>
                  <p className="font-display text-lg mt-0.5" style={{ color: m.color }}>
                    {m.value}
                  </p>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* Performance table */}
        <Panel title="Campaign Performance">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  {['Campaign', 'Acquired', 'Engaged', 'Converted', 'Eng. Rate', 'Conv. Rate', 'Revenue', 'Pipeline'].map(h => (
                    <th
                      key={h}
                      className="text-left py-2 pr-4 font-medium uppercase tracking-wider"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                      Loading...
                    </td>
                  </tr>
                ) : performance.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                      No performance data yet. Seed attribution to populate.
                    </td>
                  </tr>
                ) : performance.map((p, i) => (
                  <tr
                    key={p.campaign_source}
                    style={{ borderBottom: '1px solid var(--border-primary)' }}
                    className="hover:bg-white/2 transition-colors"
                  >
                    <td className="py-2.5 pr-4" style={{ color: 'var(--text-primary)' }}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: PALETTE[i % PALETTE.length] }}
                        />
                        {p.campaign_name}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4" style={{ color: 'var(--text-secondary)' }}>
                      {p.contacts_acquired.toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4" style={{ color: '#f59e0b' }}>
                      {p.contacts_engaged.toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4" style={{ color: '#22c55e' }}>
                      {p.contacts_converted.toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4" style={{ color: 'var(--text-secondary)' }}>
                      {fmtPct(p.engagement_rate)}
                    </td>
                    <td className="py-2.5 pr-4" style={{ color: 'var(--text-secondary)' }}>
                      {fmtPct(p.conversion_rate)}
                    </td>
                    <td className="py-2.5 pr-4" style={{ color: '#a855f7' }}>
                      {fmtMoney(p.revenue_generated)}
                    </td>
                    <td className="py-2.5 pr-4" style={{ color: 'var(--text-muted)' }}>
                      {fmtMoney(p.total_pipeline_value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Seed action */}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSeed}
              disabled={seeding}
              className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg"
              style={{
                background: 'rgba(144,185,171,0.08)',
                border: '1px solid rgba(144,185,171,0.15)',
                color: 'var(--netrun-green)',
                cursor: seeding ? 'not-allowed' : 'pointer',
                opacity: seeding ? 0.7 : 1,
              }}
            >
              <RefreshCw size={12} className={seeding ? 'animate-spin' : ''} />
              {seeding ? 'Seeding...' : 'Re-seed attribution from contacts'}
            </button>
            {seedMsg && (
              <span className="text-xs" style={{ color: 'var(--netrun-green)' }}>{seedMsg}</span>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}
